import type paper from "paper";
import type { ConstructionLine } from "./Project";

/** Translate an SVG path string by `(dx, dy)` via Paper, preserving curves.
 *  Returns the input unchanged on empty / unparseable data. */
export function translatePathData(
  d: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): string {
  if (!d) return d;
  try {
    const cp = new scope.CompoundPath({ pathData: d, insert: false });
    cp.translate(new scope.Point(dx, dy));
    return cp.pathData;
  } catch {
    return d;
  }
}

export function translatePrimitive(
  prim: ConstructionLine["primitive"],
  dx: number,
  dy: number,
): ConstructionLine["primitive"] {
  if (!prim) return prim;
  if (prim.kind === "rectangle" || prim.kind === "ellipse") {
    return { ...prim, x: prim.x + dx, y: prim.y + dy };
  }
  return {
    ...prim,
    x1: prim.x1 + dx,
    y1: prim.y1 + dy,
    x2: prim.x2 + dx,
    y2: prim.y2 + dy,
  };
}

/**
 * Move a whole construction line by (`dx`, `dy`) — every field that holds a
 * position, not just its `pathData` (#1108).
 *
 * The one place that knows the list. It used to be written out by hand at each
 * site that moves a line (move drag, snap-chain closure, rotation partner,
 * duplicate), and each copy covered a different subset: a duplicate left the
 * rounding source, rotation frame and pivot behind, so a rounded copy was
 * re-derived straight back on top of its original. The snap-chain enforcement
 * kept its own copy until #1165, which is how a rounded partner dragged along
 * by a snap sprang back while its primitive moved. A field added here reaches
 * every one of them.
 *
 * Fields that hold a position:
 *  - `pathData` and the parametric `primitive`;
 *  - `cornerRounding.sharpPathData` — the rounding pass (#100) re-derives the
 *    visible path from it on every commit;
 *  - `rawPoints` — the freehand smoothing pass (#171) re-derives from them;
 *  - `rotationCenter` (#67) and `pivotPoint` (#420) — the rotated bbox frame
 *    and the user-parked pivot;
 *  - `convertedFromPrimitive.primitive` — the shape a reversible edit restores.
 * `rotation` itself is an angle, and `bezierMemory` holds handle offsets, so
 * neither changes under a translation.
 */
export function translateConstructionLine(
  l: ConstructionLine,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): ConstructionLine {
  if (dx === 0 && dy === 0) return l;
  const shift = (p: { x: number; y: number }) => ({ x: p.x + dx, y: p.y + dy });
  return {
    ...l,
    pathData: translatePathData(l.pathData, dx, dy, scope),
    primitive: l.primitive
      ? translatePrimitive(l.primitive, dx, dy)
      : undefined,
    ...(l.cornerRounding
      ? {
          cornerRounding: {
            ...l.cornerRounding,
            sharpPathData: translatePathData(
              l.cornerRounding.sharpPathData,
              dx,
              dy,
              scope,
            ),
          },
        }
      : {}),
    ...(l.rotationCenter ? { rotationCenter: shift(l.rotationCenter) } : {}),
    ...(l.pivotPoint ? { pivotPoint: shift(l.pivotPoint) } : {}),
    ...(l.rawPoints ? { rawPoints: l.rawPoints.map(shift) } : {}),
    ...(l.convertedFromPrimitive
      ? {
          convertedFromPrimitive: {
            ...l.convertedFromPrimitive,
            primitive: translatePrimitive(
              l.convertedFromPrimitive.primitive,
              dx,
              dy,
            )!,
          },
        }
      : {}),
  };
}
